import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  TableSortLabel,
  useTheme,
} from '@mui/material';
import { formatCurrency, panelSurfaceSx } from './controlCenterUtils';
import ControlCenterInfoTooltip from './ControlCenterInfoTooltip';

/** Neutral column explanations (aligned with controlCenterDao.getTopDispatchers). */
export const TOP_DISPATCHERS_COLUMN_INFO = {
  fullName:
    'First 10 dispatchers ranked by total revenue. Click View all to see the full list. Includes every company user with the dispatcher role, even when they have no assigned jobs in the range. Job counts follow the header client filter.',
  fullNameAccessNote:
    'Click a name to open that user’s profile.',
  assignedJobs:
    'Count of jobs assigned to this dispatcher whose create date falls in the selected date range. Includes every status. Respects the header client filter.',
  completedJobs:
    'Count of jobs assigned to this dispatcher whose current status is Recall No Charge, Invoiced, Cancelled, or Invoiced/Loss, and whose completion date falls in the selected range. Completion date = invoice date for Invoiced / Invoiced-Loss; lock date for Recall No Charge / Cancelled. Respects the header client filter.',
  totalRevenue:
    'Sum of NTE for this dispatcher’s Completed Jobs in the selected range (same universe as Completed Jobs). Not proposal price; not the paid amount.',
  totalCost:
    'Sum of all technician costs plus all parts costs on this dispatcher’s Completed Jobs in the selected range (no paid-date or parts-approval filter).',
  totalProfit: 'Total Revenue minus Cost for this dispatcher’s Completed Jobs in the selected range.',
  avgTicketCost:
    'Average NTE of this dispatcher’s Completed Jobs in the selected range (same universe as Completed Jobs). Cancelled / Recall No Charge are included (NTE is often $0).',
};

const buildColumnDefs = (showAccessNotes) => [
  {
    key: 'fullName',
    label: 'Full Name',
    align: 'left',
    type: 'string',
    info: showAccessNotes
      ? `${TOP_DISPATCHERS_COLUMN_INFO.fullName} ${TOP_DISPATCHERS_COLUMN_INFO.fullNameAccessNote}`
      : TOP_DISPATCHERS_COLUMN_INFO.fullName,
  },
  { key: 'assignedJobs', label: 'Assigned Jobs', align: 'center', type: 'number', clickable: true, metric: 'created', info: TOP_DISPATCHERS_COLUMN_INFO.assignedJobs },
  { key: 'completedJobs', label: 'Completed Jobs', align: 'center', type: 'number', clickable: true, metric: 'completed', info: TOP_DISPATCHERS_COLUMN_INFO.completedJobs },
  { key: 'totalRevenue', label: 'Revenue', align: 'center', type: 'currency', info: TOP_DISPATCHERS_COLUMN_INFO.totalRevenue },
  { key: 'totalCost', label: 'Cost', align: 'center', type: 'currency', info: TOP_DISPATCHERS_COLUMN_INFO.totalCost },
  { key: 'totalProfit', label: 'Profit', align: 'center', type: 'currency', info: TOP_DISPATCHERS_COLUMN_INFO.totalProfit },
  { key: 'avgTicketCost', label: 'Avg Ticket', align: 'center', type: 'currency', info: TOP_DISPATCHERS_COLUMN_INFO.avgTicketCost },
];

const compareDispatchers = (a, b, orderBy, order, columnDefs) => {
  const dir = order === 'asc' ? 1 : -1;
  const col = columnDefs.find((c) => c.key === orderBy);
  const av = a?.[orderBy];
  const bv = b?.[orderBy];

  if (col?.type === 'string') {
    const as = String(av || '').toLowerCase();
    const bs = String(bv || '').toLowerCase();
    if (as < bs) return -1 * dir;
    if (as > bs) return 1 * dir;
    return 0;
  }

  const an = Number(av) || 0;
  const bn = Number(bv) || 0;
  if (an < bn) return -1 * dir;
  if (an > bn) return 1 * dir;
  return 0;
};

const formatCell = (dispatcher, col) => {
  const value = dispatcher[col.key];
  if (col.type === 'currency') return formatCurrency(value);
  return value ?? 0;
};

const ClickableCount = ({ value, onClick }) => {
  const numeric = Number(value) || 0;
  const clickable = typeof onClick === 'function' && numeric > 0;
  if (!clickable) return value;

  return (
    <Box
      component="button"
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{
        border: 'none',
        bgcolor: 'transparent',
        p: 0,
        m: 0,
        cursor: 'pointer',
        font: 'inherit',
        fontWeight: 600,
        color: 'inherit',
        textDecoration: 'underline',
        textDecorationColor: 'transparent',
        '&:hover': { textDecorationColor: 'currentColor', opacity: 0.85 },
      }}
    >
      {value}
    </Box>
  );
};

const DispatcherName = ({ dispatcher, canOpenUser, onUserClick }) => {
  const label = dispatcher.fullName || 'Unknown';
  if (!canOpenUser || typeof onUserClick !== 'function') return label;

  return (
    <Box
      component="button"
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onUserClick(dispatcher);
      }}
      sx={{
        border: 'none',
        bgcolor: 'transparent',
        p: 0,
        m: 0,
        cursor: 'pointer',
        font: 'inherit',
        fontWeight: 500,
        color: 'inherit',
        textAlign: 'left',
        textDecoration: 'underline',
        textDecorationColor: 'transparent',
        '&:hover': { textDecorationColor: 'currentColor', opacity: 0.85 },
      }}
    >
      {label}
    </Box>
  );
};

const DispatcherRow = ({
  index,
  dispatcher,
  isDark,
  canOpenUser,
  onUserClick,
  onJobsClick,
  columnDefs,
}) => (
  <TableRow
    sx={{
      '&:hover': { bgcolor: isDark ? '#1e293b' : '#f8fafc' },
    }}
  >
    <TableCell sx={{ fontSize: 13, color: 'text.secondary', width: 40 }}>{index + 1}</TableCell>
    {columnDefs.map((col) => {
      const raw = formatCell(dispatcher, col);
      const isProfit = col.key === 'totalProfit';
      const isCost = col.key === 'totalCost';
      let content;
      if (col.key === 'fullName') {
        content = (
          <DispatcherName
            dispatcher={dispatcher}
            canOpenUser={canOpenUser}
            onUserClick={onUserClick}
          />
        );
      } else if (col.clickable) {
        content = (
          <ClickableCount
            value={raw}
            onClick={() => onJobsClick?.(col.metric, dispatcher)}
          />
        );
      } else {
        content = raw;
      }

      return (
        <TableCell
          key={col.key}
          align={col.align}
          sx={{
            fontSize: 13,
            fontWeight: col.key === 'fullName' ? 500 : 400,
            ...(isCost ? { color: 'error.main' } : {}),
            ...(isProfit
              ? {
                  color: (dispatcher.totalProfit ?? 0) >= 0 ? 'success.main' : 'error.main',
                  fontWeight: 600,
                }
              : {}),
          }}
        >
          {content}
        </TableCell>
      );
    })}
  </TableRow>
);

const DispatchersTable = ({
  dispatchers,
  isDark,
  dense,
  orderBy,
  order,
  onRequestSort,
  canOpenUser,
  onUserClick,
  onJobsClick,
  columnDefs,
  rowOffset = 0,
}) => (
  <Table size="small" sx={{ minWidth: 860 }}>
    <TableHead>
      <TableRow>
        <TableCell sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', width: 40 }}>#</TableCell>
        {columnDefs.map((col) => (
          <TableCell
            key={col.key}
            align={col.align}
            sortDirection={orderBy === col.key ? order : false}
            sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent:
                  col.align === 'center' ? 'center' : col.align === 'right' ? 'flex-end' : 'flex-start',
                width: '100%',
              }}
            >
              <TableSortLabel
                active={orderBy === col.key}
                direction={orderBy === col.key ? order : 'asc'}
                onClick={() => onRequestSort(col.key)}
              >
                {col.label}
              </TableSortLabel>
              <ControlCenterInfoTooltip title={col.info} iconSize={13} />
            </Box>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
    <TableBody>
      {dispatchers.length === 0 ? (
        <TableRow>
          <TableCell
            colSpan={columnDefs.length + 1}
            sx={{
              textAlign: 'center',
              py: dense ? 2 : 4,
              color: 'text.secondary',
              fontSize: 13,
            }}
          >
            No dispatcher data for this period
          </TableCell>
        </TableRow>
      ) : (
        dispatchers.map((dispatcher, index) => (
          <DispatcherRow
            key={dispatcher.dispatcherId}
            index={rowOffset + index}
            dispatcher={dispatcher}
            isDark={isDark}
            canOpenUser={canOpenUser}
            onUserClick={onUserClick}
            onJobsClick={onJobsClick}
            columnDefs={columnDefs}
          />
        ))
      )}
    </TableBody>
  </Table>
);

export default function TopDispatchersPanel({
  data,
  loading,
  error,
  canOpenUser,
  onUserClick,
  onJobsClick,
  showAccessNotes = false,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const dispatchers = data?.dispatchers || [];
  const columnDefs = useMemo(() => buildColumnDefs(showAccessNotes), [showAccessNotes]);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [orderBy, setOrderBy] = useState('totalRevenue');
  const [order, setOrder] = useState('desc');

  const sortedDispatchers = useMemo(() => {
    const next = [...dispatchers];
    next.sort((a, b) => compareDispatchers(a, b, orderBy, order, columnDefs));
    return next;
  }, [dispatchers, orderBy, order, columnDefs]);

  const previewDispatchers = useMemo(() => sortedDispatchers.slice(0, 10), [sortedDispatchers]);
  const pagedDispatchers = useMemo(
    () => sortedDispatchers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedDispatchers, page, rowsPerPage],
  );

  const handleRequestSort = (key) => {
    setPage(0);
    if (orderBy === key) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setOrderBy(key);
    setOrder(key === 'fullName' ? 'asc' : 'desc');
  };

  return (
    <Box sx={panelSurfaceSx(theme)}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: { xs: 1.5, sm: 2.5 },
          pt: { xs: 1.5, sm: 2 },
          pb: 1,
        }}
      >
        <Typography sx={{ fontSize: 15, fontWeight: 500, color: 'text.secondary' }}>
          Top Dispatchers
        </Typography>
        {sortedDispatchers.length >= 10 && (
          <Button
            size="small"
            onClick={() => {
              setPage(0);
              setViewAllOpen(true);
            }}
            sx={{ textTransform: 'none', fontSize: 13 }}
          >
            View all
          </Button>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!loading && !error && (
        <Box sx={{ overflowX: 'auto', px: { xs: 1.5, sm: 2.5 }, pb: { xs: 1.5, sm: 2 } }}>
          <DispatchersTable
            dispatchers={previewDispatchers}
            isDark={isDark}
            dense
            orderBy={orderBy}
            order={order}
            onRequestSort={handleRequestSort}
            canOpenUser={canOpenUser}
            onUserClick={onUserClick}
            onJobsClick={onJobsClick}
            columnDefs={columnDefs}
          />
        </Box>
      )}

      <Dialog open={viewAllOpen} onClose={() => setViewAllOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ fontWeight: 600 }}>Dispatchers</DialogTitle>
        <DialogContent sx={{ overflowX: 'auto' }}>
          <DispatchersTable
            dispatchers={pagedDispatchers}
            isDark={isDark}
            orderBy={orderBy}
            order={order}
            onRequestSort={handleRequestSort}
            canOpenUser={canOpenUser}
            onUserClick={onUserClick}
            onJobsClick={onJobsClick}
            columnDefs={columnDefs}
            rowOffset={page * rowsPerPage}
          />
          <TablePagination
            component="div"
            count={sortedDispatchers.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[15, 30, 50, 100]}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
